import { useTranslation } from "react-i18next";
import { Button, Spinner } from "react-bootstrap";
import Form from "react-bootstrap/Form";
import BackButton from "../BackButton/BackButton";
import "./Layout.scss";
import Loader from "../Loader/Loader";
import { LAYOUT_TYPE } from "../../constants";

export interface ButtonProps {
  title: string;
  action?: Function;
  typeSubmit?: boolean;
  withSpinner?: boolean;
  variant: string;
  disabled?: boolean;
}

export interface InfoFieldsProps {
  icon: React.ReactNode;
  title: string;
}

interface LayoutProps {
  type: string;
  title?: string;
  subtitle?: string;
  backButtonTitle?: string;
  infoFields?: Array<InfoFieldsProps>;
  buttons?: Array<ButtonProps>;
  children?: React.ReactNode;
  validated?: boolean;
  onFormSubmit?: Function;
}

export const getButtons = (
  buttons: Array<ButtonProps>,
  containerClass: string,
  buttonClass: string,
  t: any
) => {
  return (
    <div className={containerClass}>
      {buttons.map((button, i) => (
        <Button
          key={i}
          type={button.typeSubmit ? "submit" : "button"}
          onClick={() => {
            if (button.action) {
              button.action();
            }
          }}
          variant={button.variant}
          className={buttonClass}
          disabled={button.disabled}
        >
          {button.withSpinner && button.disabled && (
            <Spinner
              as="span"
              animation="border"
              size="sm"
              role="status"
              aria-hidden="true"
              className="me-2"
            />
          )}
          {t(button.title)}
        </Button>
      ))}
    </div>
  );
};

export const getInfoFields = (
  infoFields: Array<InfoFieldsProps>,
  outerContainerClassName: string,
  innerContainerClassName: string
) => {
  let nonEmptyInfoFields = [];
  for (let i = 0; i < infoFields.length; i++) {
    if (infoFields[i].title) {
      nonEmptyInfoFields.push(infoFields[i]);
    }
  }
  return (
    <div className={outerContainerClassName}>
      {nonEmptyInfoFields.map((infoField, i) => (
        <div key={i} className={innerContainerClassName}>
          <div className="d-flex">{infoField.icon}</div>
          <div className="ps-086-rem">
            <span className="text-break">{infoField.title}</span>
          </div>
        </div>
      ))}
    </div>
  );
};

export default function Layout({
  type,
  title,
  subtitle,
  backButtonTitle,
  infoFields,
  buttons,
  children,
  validated,
  onFormSubmit,
}: LayoutProps) {
  const { t } = useTranslation();

  if (
    type === LAYOUT_TYPE.INNER_PAGE ||
    type === LAYOUT_TYPE.INNER_PAGE_LOADING
  ) {
    return (
      <div className="container layout-component">
        <BackButton title={backButtonTitle ? backButtonTitle : ""} />
        {type === LAYOUT_TYPE.INNER_PAGE && (
          <>
            <div className="row m-0 bg-white border-bottom">
              <div className={"p-0" + (buttons ? " col-md-6" : " col")}>
                <h3 className="ps-114-rem pt-228-rem m-0 font-weight-600 text-gray-36">
                  {title}
                </h3>
                {subtitle && (
                  <div className="ps-114-rem pt-057-rem">
                    <span className="text-gray-6">{subtitle}</span>
                  </div>
                )}
                {infoFields &&
                  getInfoFields(
                    infoFields,
                    "d-flex flex-direction-row flex-wrap ps-114-rem text-gray-6 pb-25-rem" +
                      (title || subtitle ? " pt-057-rem" : " pt-0"),
                    "small-container pe-086-rem d-flex align-items-center"
                  )}
              </div>
              {buttons && (
                <div className="col-md-6 p-0">
                  {getButtons(
                    buttons,
                    "d-flex justify-content-end pt-228-rem pe-171-rem pt-md-d-0-rem pe-md-d-1-rem pb-md-d-1-rem ps-md-d-0-rem",
                    "ms-3",
                    t
                  )}
                </div>
              )}
            </div>
            {children}
          </>
        )}
        {type === LAYOUT_TYPE.INNER_PAGE_LOADING && <Loader sizeBig={true} />}
      </div>
    );
  }

  if (type === LAYOUT_TYPE.FORM_PAGE) {
    return (
      <Form
        className="container form-container layout-component"
        noValidate
        validated={validated}
        onSubmit={(e) => {
          if (onFormSubmit) {
            onFormSubmit(e);
          }
        }}
      >
        <div className="row">
          {title && (
            <h3 className="col-sm-6 py-2-rem m-0 font-weight-600 text-gray-3">
              {t(title)}
            </h3>
          )}
          {buttons &&
            getButtons(
              buttons,
              "col-sm-6 d-flex justify-content-end",
              "my-4 ms-3",
              t
            )}
        </div>
        {children}
      </Form>
    );
  }

  return (
    <div className="container">
      <div className="d-flex justify-content-between">
        {title && (
          <div>
            <h3 className="py-2-rem m-0 font-weight-600 text-gray-3">
              {t(title)}
            </h3>
          </div>
        )}
        {buttons && getButtons(buttons, "", "mt-4 ms-3", t)}
      </div>
      {children}
    </div>
  );
}
